import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, RefreshCw, Trash2, CloudOff, Wifi, ShieldCheck, Languages, User, Users, QrCode, Bell, Lightbulb, MapPinPlus, LogOut, AlertTriangle, Building2, Store, Plus, Scale, MapPinOff } from "lucide-react";
import { toast } from "sonner";
import { getMe, api, logout } from "../lib/api";
import { downloadOfflinePack, getManifest, removeOfflinePack, fmtBytes, isOnline } from "../lib/offline";
import { BottomNav } from "../components/BottomNav";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { APP_VERSION, APP_BUILD } from "../lib/version";
import { useI18n } from "../lib/i18n";
import LanguageToggle from "../components/LanguageToggle";
import SecuritySection from "../components/SecuritySection";

export default function Settings() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [user, setUser] = useState(null);
  const [manifest, setManifest] = useState(getManifest());
  const [downloading, setDownloading] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [resetLocationOpen, setResetLocationOpen] = useState(false);

  useEffect(() => {
    getMe().then(setUser);
    const fire = () => setOnline(isOnline());
    window.addEventListener("online", fire);
    window.addEventListener("offline", fire);
    return () => {
      window.removeEventListener("online", fire);
      window.removeEventListener("offline", fire);
    };
  }, []);

  const download = async () => {
    if (!online) { toast.error(t("settings_toast_offline_connect")); return; }
    setDownloading(true);
    try {
      const m = await downloadOfflinePack();
      setManifest(m);
      toast.success(t("settings_toast_downloaded"));
    } catch (e) {
      toast.error(t("settings_toast_download_failed"));
    } finally {
      setDownloading(false);
    }
  };

  const remove = () => {
    if (!window.confirm(t("settings_confirm_remove"))) return;
    removeOfflinePack();
    setManifest(null);
    toast.success(t("settings_toast_removed"));
  };

  const addRole = async (role) => {
    if (!role || addingRole) return;
    setAddingRole(true);
    try {
      await api.post("/auth/select-role", { role });
      toast.success(role === "vendor" ? t("settings_toast_vendor_added") : t("settings_toast_business_added"));
      setAddRoleOpen(false);
      // Re-fetch user to refresh role display
      const me = await getMe();
      setUser(me);
      // Navigate to the new dashboard
      setTimeout(() => {
        window.location.assign(role === "vendor" ? "/vendor" : "/business");
      }, 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("settings_toast_role_failed"));
    } finally {
      setAddingRole(false);
    }
  };

  return (
    <main data-testid="settings-page" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <Link to="/" data-testid="settings-back" className="btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/></Link>
        <div>
          <div className="eyebrow">{t("settings_profile_eyebrow")}</div>
          <h1 className="font-display text-3xl mt-1 text-foreground">{t("settings_title")}</h1>
        </div>
      </header>

      {user && (
        <section className="mx-5 lux-card p-5 flex items-center gap-4" data-testid="settings-profile-card">
          {user.profile_photo ? (
            <img src={user.profile_photo} alt="" className="w-14 h-14 rounded-full object-cover ring-1 ring-[rgba(232,184,75,0.5)]" />
          ) : (
            <span className="w-14 h-14 rounded-full grid place-items-center bg-[hsl(var(--surface-elevated))] text-gold text-xs">
              {(user.display_name || user.email || "?").slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-foreground truncate">{user.display_name}</div>
            <div className="text-[11px] text-foreground/55 truncate">{user.email}</div>
            <div className="eyebrow mt-1">{t("settings_role_prefix")} {t(`role_${user.role}`) || user.role}</div>
            {user.terms_accepted_at && (
              <div
                data-testid="settings-terms-accepted"
                className="mt-1.5"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}
              >
                {t("settings_terms_accepted_prefix")} {formatAcceptedDate(user.terms_accepted_at, lang)}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="mt-8 px-5" data-testid="settings-language-section">
        <div className="eyebrow mb-3">{t("settings_language")}</div>
        <div className="lux-card p-4 flex items-center justify-between">
          <div>
            <div className="text-foreground" style={{ fontSize: 16, fontFamily: "'DM Sans', sans-serif" }}>
              {t("settings_language_label")}
            </div>
            <div className="text-[12px] text-foreground/55 mt-0.5">
              {t("settings_language_hint")}
            </div>
          </div>
          <LanguageToggle size="md" testIdPrefix="settings-lang" />
        </div>
      </section>

      {/* Offline Havana */}
      <section className="mx-5 mt-5 lux-card overflow-hidden" data-testid="offline-card">
        <div className="px-5 py-5 border-b border-gold-soft flex items-center gap-3">
          <span className="w-10 h-10 grid place-items-center rounded-full border border-gold-strong text-gold bg-[rgba(232,184,75,0.06)]">
            <Download className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <div className="font-display text-2xl text-foreground leading-tight">{t("settings_offline_title")}</div>
            <div className="text-[11px] text-foreground/55">{t("settings_offline_sub")}</div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          <Row label={t("settings_offline_status_label")} value={
            manifest?.downloaded
              ? <span className="text-gold inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={2}/>{t("settings_offline_downloaded")}</span>
              : <span className="text-foreground/55">{t("settings_offline_not_downloaded")}</span>
          } />
          {manifest?.downloaded && (
            <>
              <Row label={t("settings_offline_last_updated")} value={<span className="font-mono text-xs">{manifest.updated_at?.slice(0, 19).replace("T", " · ")}</span>} />
              <Row label={t("settings_offline_storage_used")} value={<span className="font-mono text-xs">{fmtBytes(manifest.bytes || 0)}</span>} />
              <Row label={t("settings_offline_cached")} value={<span className="font-mono text-xs text-foreground/70">{manifest.counts?.businesses || 0} {t("settings_offline_places_unit")} · {manifest.counts?.favorites || 0} {t("settings_offline_favs_unit")} · {manifest.counts?.visits || 0} {t("settings_offline_visits_unit")}</span>} />
              <Row label={t("settings_offline_map_tiles")} value={
                !process.env.REACT_APP_MAPBOX_TOKEN
                  ? <span className="text-foreground/55">{t("settings_offline_pending_token")}</span>
                  : manifest.mapbox_tiles === "active"
                    ? <span className="text-gold inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={2}/>{t("settings_offline_active")}{manifest.mapbox_tiles_queued ? ` · ${manifest.mapbox_tiles_queued} ${t("settings_offline_tiles_unit")}` : ""}</span>
                    : <span className="text-gold/80">{t("settings_offline_queued")}</span>
              } />
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gold-soft flex flex-col gap-2">
          {!manifest?.downloaded ? (
            <button data-testid="offline-download" onClick={download} disabled={downloading || !online} className="btn-gold py-3">
              {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
              {t("settings_download_btn")}
            </button>
          ) : (
            <div className="flex gap-2">
              <button data-testid="offline-update" onClick={download} disabled={downloading || !online} className="btn-outline py-3 flex-1 text-xs">
                {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={1.5} />}
                {t("settings_update_btn")}
              </button>
              <button data-testid="offline-remove" onClick={remove} className="btn-danger py-3 flex-1 text-xs">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("settings_remove_btn")}
              </button>
            </div>
          )}
          {!online && (
            <div className="text-[11px] text-gold inline-flex items-center gap-1.5 mt-2"><CloudOff className="w-3 h-3" strokeWidth={2}/>{t("settings_offline_msg")}</div>
          )}
        </div>
      </section>

      <section className="mx-5 mt-5 lux-card divide-y divide-[rgba(232,184,75,0.12)]">
        <NavRow to="/friends" icon={Users} label={t("settings_link_friends")} hint={t("settings_link_friends_hint")} testid="link-friends" />
        <NavRow to="/my-qr" icon={QrCode} label={t("settings_link_my_qr")} hint={t("settings_link_my_qr_hint")} testid="link-my-qr" />
        <NavRow to="/notifications" icon={Bell} label={t("settings_link_notifications")} hint={t("settings_link_notifications_hint")} testid="link-notifications" />
        <NavRow to="/tips" icon={Lightbulb} label={t("settings_link_tips")} hint={t("settings_link_tips_hint")} testid="link-tips" />
        <NavRow to="/suggest" icon={MapPinPlus} label={t("settings_link_suggest")} hint={t("settings_link_suggest_hint")} testid="link-suggest" />
      </section>

      <section className="mx-5 mt-5 lux-card divide-y divide-[rgba(232,184,75,0.12)]">
        <NavRow to="/settings/privacy" icon={ShieldCheck} label={t("settings_link_privacy")} hint={t("settings_link_privacy_hint")} testid="link-privacy" />
        <NavRow to="/settings/privacy" icon={Languages} label={t("settings_link_language_row")} hint={t("settings_link_language_row_hint")} testid="link-lang" />
        <NavRow to="/settings/privacy" icon={User} label={t("settings_link_account")} hint={t("settings_link_account_hint")} testid="link-account" />
        <NavRow
          icon={MapPinOff}
          label={t("settings_reset_location_label")}
          hint={t("settings_reset_location_hint")}
          testid="reset-location-row"
          onClick={() => setResetLocationOpen(true)}
        />
        <NavRow icon={Wifi} label={t("settings_link_connection")} hint={online ? t("settings_online") : t("settings_offline_word")} testid="link-connection" />
      </section>

      {user && user.role !== "admin" && (
        <section className="mx-5 mt-5">
          <button
            data-testid="add-role-trigger"
            onClick={() => setAddRoleOpen(true)}
            className="w-full text-left px-5 py-4 flex items-center gap-4 border border-gold-soft hover:border-gold-strong transition-colors"
            style={{ background: "#1D1D1D", minHeight: 60 }}
          >
            <span className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--surface-elevated))] text-gold border border-gold-soft">
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">{t("settings_add_role_btn")}</div>
              <div className="text-[11px] text-foreground/55">
                {t("settings_add_role_hint")}
              </div>
            </div>
            <span className="text-foreground/30">›</span>
          </button>
        </section>
      )}

      {/* Phase 2b — Security (Backup Email, Phone Change, Delete Account) */}
      {user && <SecuritySection user={user} onChanged={() => getMe().then(setUser)} />}

      <section className="mt-10 px-5">
        <div className="eyebrow mb-3">{t("settings_legal_eyebrow")}</div>
        <div className="lux-card divide-y divide-[rgba(232,184,75,0.12)]">
          <NavRow to="/privacy" icon={ShieldCheck} label={t("settings_privacy_policy")} hint={t("settings_privacy_hint")} testid="link-legal-privacy" />
          <NavRow to="/terms" icon={Scale} label={t("settings_terms_service")} hint={t("settings_terms_hint")} testid="link-legal-terms" />
        </div>
      </section>

      <section className="mt-10 px-5">
        <button
          data-testid="signout-trigger"
          onClick={() => setSignOutOpen(true)}
          className="btn-outline w-full justify-center"
          style={{ minHeight: 52 }}
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("settings_signout")}
        </button>
        <div
          data-testid="app-version"
          className="text-center text-[11px] font-mono text-foreground/45 mt-4 tracking-widest"
        >
          {APP_VERSION} · {APP_BUILD}
        </div>
        <div
          data-testid="settings-copyright"
          className="text-center text-[10px] text-foreground/35 mt-2 tracking-widest uppercase"
        >
          © 2025 Habano App LLC
        </div>
      </section>

      <AlertDialog open={resetLocationOpen} onOpenChange={setResetLocationOpen}>
        <AlertDialogContent
          data-testid="reset-location-confirm"
          className="bg-surface border border-gold-soft"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-foreground flex items-center gap-2">
              <MapPinOff className="w-4 h-4 text-gold" strokeWidth={1.5} />
              {t("settings_reset_location_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/55 text-sm" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1.5 }}>
              {t("settings_reset_location_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-location-cancel" className="btn-outline">
              {t("settings_reset_location_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-location-confirm-btn"
              className="btn-gold"
              onClick={(e) => {
                e.preventDefault();
                try {
                  localStorage.removeItem("habano.location.prompted");
                  localStorage.removeItem("habano.location.denied");
                  localStorage.removeItem("habano.gps.last");
                } catch { /* ignore storage errors */ }
                setResetLocationOpen(false);
                toast.success(t("settings_reset_location_toast"));
              }}
            >
              {t("settings_reset_location_confirm_cta")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={signOutOpen} onOpenChange={setSignOutOpen}>
        <AlertDialogContent
          data-testid="signout-confirm"
          className="bg-surface border border-gold-soft"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-gold" strokeWidth={1.5} />
              {t("settings_signout_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/55 text-sm">
              {t("settings_signout_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="signout-cancel" className="btn-outline">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="signout-confirm-btn"
              disabled={signingOut}
              className="btn-gold"
              onClick={async (e) => {
                e.preventDefault();
                setSigningOut(true);
                try {
                  await logout();
                } catch { /* ignore */ }
                try {
                  // Clear any auth-related local storage
                  for (const k of Object.keys(localStorage)) {
                    if (/auth|session|habano\.user|habano\.welcome|habano\.lang/.test(k)) localStorage.removeItem(k);
                  }
                } catch { /* ignore */ }
                toast.success(t("settings_toast_signed_out"));
                setSignOutOpen(false);
                navigate("/login", { replace: true });
                // Hard reload so all in-memory state resets
                setTimeout(() => { window.location.assign("/login"); }, 50);
              }}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5}/> {t("settings_signout")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add another role dialog */}
      <Dialog open={addRoleOpen} onOpenChange={setAddRoleOpen}>
        <DialogContent
          data-testid="add-role-dialog"
          className="border border-gold-strong p-0 max-w-md w-[92vw] rounded-none"
          style={{ background: "#171717" }}
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gold-soft">
            <DialogTitle className="font-display text-2xl text-foreground">
              {t("settings_add_role_dialog_title")}
            </DialogTitle>
            <DialogDescription className="text-xs text-foreground/55 mt-1">
              {t("settings_add_role_dialog_sub")}
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 py-5 flex flex-col gap-3">
            <button
              data-testid="add-role-vendor"
              type="button"
              disabled={addingRole || user?.role === "vendor"}
              onClick={() => addRole("vendor")}
              className="inline-flex items-center gap-3 px-5 py-4 border border-gold-strong text-gold hover:bg-[rgba(232,184,75,0.08)] disabled:opacity-40 transition-colors text-sm uppercase tracking-widest"
              style={{ minHeight: 56 }}
            >
              <Store className="w-5 h-5" strokeWidth={1.5} />
              {t("settings_add_role_vendor")}
              {user?.role === "vendor" && <span className="ml-auto text-[10px] text-foreground/55">{t("settings_add_role_current")}</span>}
            </button>
            <button
              data-testid="add-role-business"
              type="button"
              disabled={addingRole || user?.role === "business"}
              onClick={() => addRole("business")}
              className="inline-flex items-center gap-3 px-5 py-4 bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90 disabled:opacity-40 transition-opacity text-sm uppercase tracking-widest"
              style={{ minHeight: 56 }}
            >
              <Building2 className="w-5 h-5" strokeWidth={1.5} />
              {t("settings_add_role_business")}
              {user?.role === "business" && <span className="ml-auto text-[10px]">{t("settings_add_role_current")}</span>}
            </button>
          </div>
          <DialogFooter className="px-5 py-4 border-t border-gold-soft">
            <button
              data-testid="add-role-cancel"
              type="button"
              onClick={() => setAddRoleOpen(false)}
              className="btn-outline text-xs ml-auto"
              style={{ minHeight: 48 }}
            >
              {t("cancel")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="eyebrow">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function formatAcceptedDate(iso, lang = "en") {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const locale = lang === "es" ? "es-ES" : "en-US";
    return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function NavRow({ to, icon: Icon, label, hint, testid, onClick }) {
  const inner = (
    <>
      <span className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--surface-elevated))] text-gold border border-gold-soft">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-[11px] text-foreground/55">{hint}</div>
      </div>
      <span className="text-foreground/30">›</span>
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-white/[0.03]">
        {inner}
      </Link>
    );
  }
  return (
    <button onClick={onClick} data-testid={testid} className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-white/[0.03]">
      {inner}
    </button>
  );
}
